/**
 * Hook для отримання метаданих поточного роута
 * Використовує location та дані з RouteKernelLoader
 */

import { useMemo } from 'react'
import { useLocation } from 'react-router'

import type { RouteInfo, RouteKernelLoader } from '../Commands/Loaders/RouteKernelLoader'
import { getKernelSafe } from '../Services/KernelRegistry'

interface RouteMetadata {
    template?: string
    title?: string
    layout?: string
    key?: string
    group?: string
    isResourceGenerated?: boolean
    routeInfo?: RouteInfo
    accessFn?: (options: { context: any }) => boolean
    templateError403?: React.ComponentType
}

/**
 * Hook для отримання метаданих поточного роута
 */
export function useCurrentRouteMetadata(): RouteMetadata {
    const location = useLocation()

    const routeMetadata = useMemo(() => {
        try {
            const kernel = getKernelSafe()

            if (!kernel) {
                // Kernel ще не ініціалізовано, повертаємо пусті метадані
                return {}
            }

            if (!kernel.getService) {
                console.warn('❌ kernel.getService недоступний')
                return {}
            }

            const routeLoader = (kernel as any).getLoader
                ? ((kernel as any).getLoader('route') as RouteKernelLoader)
                : undefined

            if (!routeLoader) {
                console.warn('❌ RouteKernelLoader не знайдено')
                return {}
            }

            if (typeof routeLoader.findRouteByPath !== 'function') {
                console.warn('❌ findRouteByPath не є функцією')
                return {}
            }

            let currentRoute: RouteInfo | undefined = routeLoader.findRouteByPath(location.pathname)

            if (!currentRoute && location.pathname !== '/') {
                const basePath = location.pathname.split('/').slice(0, -1).join('/') || '/'
                currentRoute = routeLoader.findRouteByPath(basePath)
            }

            if (!currentRoute) {
                // Виводимо попередження тільки в режимі розробки
                if (import.meta.env.DEV) {
                    console.warn('❌ Роут не знайдено для шляху:', location.pathname)
                }
                return {}
            }

            const metadata = {
                template: currentRoute.template || currentRoute.config?.template,
                title: currentRoute.config?.title,
                layout: currentRoute.config?.layout,
                key: currentRoute.key,
                group: currentRoute.group,
                isResourceGenerated: currentRoute.isResourceGenerated,
                routeInfo: currentRoute,
                accessFn: currentRoute.config?.accessFn,
                templateError403: currentRoute.config?.templateError403,
            }

            return metadata
        } catch (error) {
            console.warn('Помилка отримання метаданих роута:', error)
            return {}
        }
    }, [location.pathname])

    return routeMetadata
}

/**
 * Hook для отримання template поточного роута
 */
export function useCurrentRouteTemplate(defaultTemplate: string = 'default'): string {
    const { template } = useCurrentRouteMetadata()
    const result = template || defaultTemplate
    return result
}

/**
 * Hook для отримання заголовка поточного роута
 */
export function useCurrentRouteTitle(): string | undefined {
    const { title } = useCurrentRouteMetadata()
    return title
}
